/**
 * Money display for the dashboard.
 *
 * WHY: the API encodes every monetary amount as an integer number of minor units in a JSON
 * *string* (bigint). Those strings must never become a JS number — 2^53 and binary fractions
 * both lie. So the scaling from minor units to a decimal is done with string surgery through
 * the currency's own ISO-4217 exponent, and the resulting exact decimal string is handed to
 * Intl.NumberFormat (which accepts a numeric string and formats its mathematical value
 * exactly). No float divide, no Number(), anywhere on this path.
 *
 * The exponent table mirrors packages/core/src/money/index.ts — the backend parses decimals
 * into minor units with the same table, so display must scale back with the same one.
 */

/** ISO-4217 exponents that differ from the default of 2. Mirrors packages/core/src/money. */
const CURRENCY_EXPONENT_OVERRIDES: Readonly<Record<string, number>> = {
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0, RWF: 0,
  UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  CLF: 4, UYW: 4,
};
const DEFAULT_EXPONENT = 2;

/** US seller base; a fixed locale also keeps the unit tests deterministic. */
const DISPLAY_LOCALE = "en-US";
/** Rendered when the API sends null (e.g. a discrepancy group with no amount). */
export const NO_VALUE = "—";

const MINOR_PATTERN = /^[+-]?\d+$/;
const CURRENCY_PATTERN = /^[A-Za-z]{3}$/;

export type Minor = string;

export function isCurrencyCode(currency: string | null | undefined): currency is string {
  return typeof currency === "string" && CURRENCY_PATTERN.test(currency);
}

export function currencyExponent(currency: string | null | undefined): number {
  if (!isCurrencyCode(currency)) return DEFAULT_EXPONENT;
  return CURRENCY_EXPONENT_OVERRIDES[currency.toUpperCase()] ?? DEFAULT_EXPONENT;
}

export function isValidMinor(minor: string | null | undefined): minor is Minor {
  return typeof minor === "string" && MINOR_PATTERN.test(minor);
}

/** -1 / 0 / 1 without leaving string space — used to colour negative amounts. */
export function minorSign(minor: string | null | undefined): -1 | 0 | 1 {
  if (!isValidMinor(minor)) return 0;
  if (minor.startsWith("-")) return /[1-9]/.test(minor) ? -1 : 0;
  return /[1-9]/.test(minor) ? 1 : 0;
}

/**
 * Exact minor units → decimal string ("−38", USD → "-0.38"). String-only, so a value far
 * beyond Number.MAX_SAFE_INTEGER survives untouched.
 */
export function minorToDecimalString(minor: Minor, currency: string | null | undefined): string {
  if (!isValidMinor(minor)) throw new RangeError(`not an integer minor-unit string: ${String(minor)}`);
  const negative = minor.startsWith("-");
  const digits = minor.replace(/^[+-]/, "");
  const exponent = currencyExponent(currency);
  const sign = negative && /[1-9]/.test(digits) ? "-" : "";
  if (exponent === 0) return `${sign}${stripLeadingZeros(digits)}`;
  const padded = digits.padStart(exponent + 1, "0");
  const whole = stripLeadingZeros(padded.slice(0, padded.length - exponent));
  return `${sign}${whole}.${padded.slice(padded.length - exponent)}`;
}

function stripLeadingZeros(digits: string): string {
  const trimmed = digits.replace(/^0+/, "");
  return trimmed === "" ? "0" : trimmed;
}

const formatterCache = new Map<string, Intl.NumberFormat>();

function formatter(currency: string | null | undefined, signed: boolean): Intl.NumberFormat {
  const exponent = currencyExponent(currency);
  const code = isCurrencyCode(currency) ? currency.toUpperCase() : "";
  const key = `${code}|${exponent}|${signed}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const created = new Intl.NumberFormat(DISPLAY_LOCALE, {
    ...(code ? { style: "currency" as const, currency: code, currencyDisplay: "narrowSymbol" as const } : {}),
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
    ...(signed ? { signDisplay: "exceptZero" as const } : {}),
  });
  formatterCache.set(key, created);
  return created;
}

/**
 * Minor units + ISO code → display string. Null amount renders as NO_VALUE; a malformed amount
 * is echoed raw rather than thrown, because a contract drift in one cell must not blank a page.
 */
export function formatMoney(minor: Minor | null | undefined, currency: string | null | undefined, signed = false): string {
  if (minor === null || minor === undefined) return NO_VALUE;
  if (!isValidMinor(minor)) return isCurrencyCode(currency) ? `${minor} ${currency.toUpperCase()}` : minor;
  const decimal = minorToDecimalString(minor, currency) as Intl.StringNumericLiteral;
  return formatter(currency, signed).format(decimal);
}

/** Same, but positive amounts carry an explicit "+" — for fee/adjustment columns. */
export function formatSignedMoney(minor: Minor | null | undefined, currency: string | null | undefined): string {
  return formatMoney(minor, currency, true);
}

const countFormatter = new Intl.NumberFormat(DISPLAY_LOCALE, { maximumFractionDigits: 0 });

/** Counts also arrive as bigint strings (count(*)); group them without parsing to a number. */
export function formatCount(value: string | null | undefined): string {
  if (!isValidMinor(value)) return NO_VALUE;
  return countFormatter.format(value as Intl.StringNumericLiteral);
}
